import { useCallback, useRef, useState } from "react"
import { securePrintService } from "../Service/securePrintService"
import { connectToQzTray, listPrinters, silentPrint, isQzRunning } from "../utils/qzTrayService"

export type SecurePrintPhase =
  | "idle"
  | "checking-printer"
  | "uploading"
  | "retrieving"
  | "printing"
  | "success"
  | "error"

export interface SecurePrintState {
  phase: SecurePrintPhase
  message: string
  printJobId: number | null
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [meta, base64] = dataUrl.split(",")
  const mimeMatch = /data:(.*);base64/.exec(meta)
  const mime = mimeMatch ? mimeMatch[1] : "image/png"
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return new Blob([bytes], { type: mime })
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result as string
      // Strip the data: prefix — QZ Tray wants raw base64.
      const base64 = result.substring(result.indexOf(",") + 1)
      resolve(base64)
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export interface UseSecurePrintOptions {
  workstation?: string
}

/**
 * Everything needed for "operator clicks Print Badge" -> silent, secure
 * print via QZ Tray, with no browser dialog, no download, and no temporary
 * file left behind on the server.
 */
export function useSecurePrint(options: UseSecurePrintOptions = {}) {
  const [state, setState] = useState<SecurePrintState>({
    phase: "idle",
    message: "",
    printJobId: null,
  })
  // Guards against a slow double-click firing two print jobs for one badge.
  const inFlight = useRef(false)

  const reset = useCallback(() => {
    setState({ phase: "idle", message: "", printJobId: null })
  }, [])

  const checkQzAvailability = useCallback(async (): Promise<{ ok: boolean; message?: string }> => {
    try {
      await connectToQzTray()
      return { ok: true }
    } catch (err: any) {
      return {
        ok: false,
        message:
          err?.message ||
          "QZ Tray is not installed or not running on this workstation. Install/start QZ Tray and try again.",
      }
    }
  }, [])

  const discoverPrinters = useCallback(async () => {
    return listPrinters()
  }, [])

  /**
   * badgeImageDataUrl: the PNG data URL produced by the existing, unchanged
   * canvas-based badge renderer (professional-badge-designer.tsx). It never
   * touches disk and is discarded from memory as soon as the print job
   * either succeeds or fails.
   */
  const printBadgeSecurely = useCallback(
    async (params: {
      badgeTrackingId: number | string
      badgeImageDataUrl: string
      printerName: string
      personName?: string
    }) => {
      if (inFlight.current) return
      inFlight.current = true
      let objectUrlsToRevoke: string[] = []
      let createdPrintJobId: number | null = null

      try {
        setState({ phase: "checking-printer", message: "Checking QZ Tray connection...", printJobId: null })
        if (!params.printerName) {
          throw new Error("No printer selected. Choose a printer before printing.")
        }
        const qzCheck = await checkQzAvailability()
        if (!qzCheck.ok) {
          throw new Error(qzCheck.message)
        }

        setState((s) => ({ ...s, phase: "uploading", message: "Preparing badge for printing..." }))
        const fileBlob = dataUrlToBlob(params.badgeImageDataUrl)
        const filename = `badge-${(params.personName || "print").replace(/\s+/g, "-")}-${Date.now()}.png`

        const jobResponse = await securePrintService.createPrintJob({
          badgeTrackingId: params.badgeTrackingId,
          file: fileBlob,
          filename,
          printer: params.printerName,
          workstation: options.workstation || window.location.hostname,
        })

        if (!jobResponse.success) {
          throw new Error(jobResponse.message || "Failed to create the print job.")
        }
        createdPrintJobId = jobResponse.print_job_id
        setState((s) => ({ ...s, printJobId: createdPrintJobId, message: "Retrieving badge for the printer..." }))

        setState((s) => ({ ...s, phase: "retrieving" }))
        const printableBlob = await securePrintService.fetchPrintableFile(
          jobResponse.print_job_id,
          jobResponse.print_token
        )
        const base64Data = await blobToBase64(printableBlob)

        setState((s) => ({ ...s, phase: "printing", message: `Sending to ${params.printerName}...` }))
        await silentPrint({
          printerName: params.printerName,
          base64Data,
          format: "image",
        })

        await securePrintService.reportCompleted(jobResponse.print_job_id, params.printerName)

        setState({ phase: "success", message: "Badge printed successfully.", printJobId: jobResponse.print_job_id })
      } catch (err: any) {
        const message = err?.message || "Printing failed."
        if (createdPrintJobId) {
          try {
            await securePrintService.reportFailed(createdPrintJobId, message)
          } catch {
            // Best-effort — the server-side cleanup job will expire this job either way.
          }
        }
        setState({ phase: "error", message, printJobId: createdPrintJobId })
      } finally {
        // Release any Blob/object URLs created along the way so we never
        // leak browser memory for something the operator will never see.
        objectUrlsToRevoke.forEach((url) => URL.revokeObjectURL(url))
        inFlight.current = false
      }
    },
    [checkQzAvailability, options.workstation]
  )

  return {
    state,
    reset,
    discoverPrinters,
    checkQzAvailability,
    isQzRunning,
    printBadgeSecurely,
  }
}
